import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import _ from 'lodash';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { EXTRA_PRODUCT_SPECS_BY_CODE } from 'src/constants/product';
import FemaleActive from '../assets/icon/female-active.svg';
import FemaleInactive from '../assets/icon/female-inctive.svg';
import MaleActive from '../assets/icon/male-active.svg';
import MaleInactive from '../assets/icon/male-inactive.svg';
import NonSmokerActive from '../assets/icon/non-smoker-active.svg';
import NonSmokerInactive from '../assets/icon/non-smoker-inactive.svg';
import SmokerActive from '../assets/icon/smoker-active.svg';
import SmokerInactive from '../assets/icon/smoker-inactive.svg';
import { FEMALE, MALE, NON_SMOKER, SMOKER } from '../constants';
import { useQuotationContext } from '../contexts/QuotationContext';
import { Product, ProductVariable } from '../graphql';
import OptionSlider from './OptionSlider';
import SumAssured from './SumAssured';

const ICON_SIZE = 44;
const GENDER_OPTIONS = [
  {
    value: MALE,
    activeIcon: <MaleActive width={ICON_SIZE} height={ICON_SIZE} />,
    inactiveIcon: <MaleInactive width={ICON_SIZE} height={ICON_SIZE} />,
  },
  {
    value: FEMALE,
    activeIcon: <FemaleActive width={ICON_SIZE} height={ICON_SIZE} />,
    inactiveIcon: <FemaleInactive width={ICON_SIZE} height={ICON_SIZE} />,
  },
];
const SMOKER_OPTIONS = [
  {
    value: NON_SMOKER,
    activeIcon: <NonSmokerActive width={ICON_SIZE} height={ICON_SIZE} />,
    inactiveIcon: <NonSmokerInactive width={ICON_SIZE} height={ICON_SIZE} />,
  },
  {
    value: SMOKER,
    activeIcon: <SmokerActive width={ICON_SIZE} height={ICON_SIZE} />,
    inactiveIcon: <SmokerInactive width={ICON_SIZE} height={ICON_SIZE} />,
  },
];

const useStyles = makeStyles(() => ({
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'start',
  },
}));

type Props = {
  products: Product[];
  variables: ProductVariable[] | null;
};

const FormPanel = ({ products, variables }: Props) => {
  const { values, handleChange, style } = useQuotationContext();
  const { fieldContainer } = useStyles();
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down('xs'));

  const [smoke, gender, sumAssured] = useMemo(() => {
    const someProductIs = (products: Product[], key: string) => {
      return products.some(
        product => EXTRA_PRODUCT_SPECS_BY_CODE[product.code]?.[key],
      );
    };
    return [
      someProductIs(products, 'smoke'),
      someProductIs(products, 'gender'),
      someProductIs(products, 'sumAssured'),
    ];
  }, [products]);

  const min = Math.min(
    ...products.map(product => {
      let minAgeValue: number;
      switch (product.min_issue_age_unit) {
        case 'days':
          minAgeValue = Math.floor(product.min_issue_age_value / 365);
          break;
        case 'years':
          minAgeValue = product.min_issue_age_value;
          break;
        default:
          minAgeValue = 0;
      }
      return minAgeValue;
    }),
  );
  const max = Math.max(...products.map(product => product.max_issue_age_value));
  const AGE_OPTIONS = _.range(
    min !== Infinity ? min : 0,
    max !== -Infinity ? max + 1 : 99,
  );

  const isAgeValid = AGE_OPTIONS.includes(values.age);

  const deductibleVariable = variables
    ? variables.find(
        productAttribute => productAttribute.attribute.key === 'deductible',
      )
    : null;

  return (
    <Box px={1}>
      <Grid container direction="column" spacing={2}>
        <Grid container item spacing={2} alignItems="stretch">
          {style !== 'resultOnly' && (
            <>
              {(gender || style === 'formOnly') && (
                <>
                  <Grid item xs={4} sm={4} className={fieldContainer}>
                    <Typography variant="body1">
                      <FormattedMessage id="gender" />
                    </Typography>
                    <RadioGroup
                      name="gender"
                      value={values.gender}
                      onChange={handleChange}
                      row
                      style={{ flexWrap: 'nowrap' }}
                    >
                      {GENDER_OPTIONS.map(
                        ({
                          value,
                          activeIcon: ActiveIcon,
                          inactiveIcon: InactiveIcon,
                        }) => (
                          <FormControlLabel
                            style={
                              isXS
                                ? { marginRight: theme.spacing(1) }
                                : undefined
                            }
                            label={null}
                            key={value}
                            value={value}
                            control={
                              <Radio
                                style={{ padding: 4 }}
                                disableRipple
                                checkedIcon={ActiveIcon}
                                icon={InactiveIcon}
                              />
                            }
                          />
                        ),
                      )}
                    </RadioGroup>
                  </Grid>
                </>
              )}
              <Grid
                item
                xs={4}
                className={fieldContainer}
                style={isXS ? { paddingRight: 0 } : undefined}
              >
                <Typography variant="body1">
                  <FormattedMessage id="age" />
                </Typography>
                <Select
                  native
                  variant="outlined"
                  name="age"
                  value={values.age}
                  onChange={handleChange}
                  input={
                    <OutlinedInput
                      labelWidth={0}
                      style={{ marginTop: 6, padding: 2 }}
                    />
                  }
                  renderValue={value => (
                    <Typography variant="body2">{value}</Typography>
                  )}
                >
                  {!isAgeValid && (
                    <option key={undefined} value={undefined}>
                      {values.age}
                    </option>
                  )}
                  {AGE_OPTIONS.map(age => (
                    <option key={age} value={age}>
                      {age}
                    </option>
                  ))}
                </Select>
              </Grid>
              {(smoke || style === 'formOnly') && (
                <>
                  <Grid item xs={4} className={fieldContainer}>
                    <Typography variant="body1">
                      <FormattedMessage id="smokingStatus" />
                    </Typography>
                    <RadioGroup
                      name="smoker"
                      value={values.smoker}
                      onChange={handleChange}
                      row
                      style={{ flexWrap: 'nowrap' }}
                    >
                      {SMOKER_OPTIONS.map(
                        ({
                          value,
                          activeIcon: ActiveIcon,
                          inactiveIcon: InactiveIcon,
                        }) => (
                          <FormControlLabel
                            style={
                              isXS
                                ? { marginRight: theme.spacing(1) }
                                : undefined
                            }
                            label={null}
                            key={value}
                            value={value}
                            control={
                              <Radio
                                style={{ padding: 4 }}
                                disableRipple
                                checkedIcon={ActiveIcon}
                                icon={InactiveIcon}
                              />
                            }
                          />
                        ),
                      )}
                    </RadioGroup>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
        {sumAssured && (
          <Grid item xs={12}>
            <SumAssured
              age={values.age}
              value={values.sumAssured}
              handleChange={handleChange}
            />
          </Grid>
        )}
        {deductibleVariable && (
          <Grid item xs={12}>
            <OptionSlider productVariable={deductibleVariable} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default FormPanel;
